import type { ServiceContract, ServiceVisit, ContractInvoice } from '../types';
// La misma función con la que la pantalla de Cobros emite la factura. Se importa
// en vez de copiarse: copiar la fórmula arreglaría la cifra de hoy y dejaría la
// puerta abierta a que las dos vuelvan a separarse mañana. Ver la sección «La
// unidad de un contrato Monthly es el MES» más abajo.
import { amountForPeriod } from './contractBilling';

/** Average number of service visits per month, by contract frequency. */
const VISITS_PER_MONTH: Record<string, number> = {
    Weekly: 4.33,
    'Bi-Weekly': 2.17,
};

/**
 * Monthly recurring revenue for a single service contract.
 * Monthly-billed contracts use their flat rate; per-visit contracts are
 * prorated by how often they're visited each month.
 */
export function contractMRR(contract: ServiceContract): number {
    if (contract.billingType === 'Monthly') {
        return contract.monthlyRate || 0;
    }
    const visitsPerMonth = VISITS_PER_MONTH[contract.frequency] ?? 1;
    return (contract.visitRate || 0) * visitsPerMonth;
}

/**
 * Revenue earned from a single completed service visit. Per-visit contracts
 * use their flat visit rate; monthly-billed contracts divide the monthly rate
 * across the expected number of visits that month.
 *
 * SUPERADA PARA CONTRATOS `Monthly`, y a propósito no se borra: sigue siendo la
 * respuesta correcta —la tarifa por visita— cuando `billingType` es 'PerVisit',
 * que es como la usan los motores. La rama `Monthly` divide por 4,33 un precio
 * que el cliente paga entero, y ningún motor la llama ya: un mes de contrato
 * mensual se valora con `contractPeriods` / `unbilledEarnings`, y una fila
 * suelta con `visitShareOfMonth`. Si acabas aquí buscando cuánto vale una visita
 * de un contrato mensual, la respuesta es que no vale nada por sí sola — vale el
 * mes.
 */
export function perVisitRevenue(contract: ServiceContract): number {
    if (contract.billingType === 'Monthly') {
        const visitsPerMonth = VISITS_PER_MONTH[contract.frequency] ?? 1;
        return (contract.monthlyRate || 0) / visitsPerMonth;
    }
    return contract.visitRate || 0;
}

/** Total monthly recurring revenue across all Active service contracts. */
export function totalServiceMRR(contracts: ServiceContract[]): number {
    return contracts
        .filter(c => c.status === 'Active')
        .reduce((sum, c) => sum + contractMRR(c), 0);
}

// ── Invoiced months vs. estimated months ────────────────────────
//
// Valuing service work by completed visits is an ESTIMATE, and a rough one:
// a Monthly contract's flat rate is divided by an assumed 4.33 visits, so a
// five-visit month reports more revenue than the client was ever billed.
//
// Once a month has a contract invoice, that invoice is the record of what the
// month is worth, and the per-visit estimate must stand aside for it. These
// helpers mark which (contract, month) pairs have been billed, so every caller
// applies the same precedence and the Finance totals, the Dashboard chart and
// the Billing tab cannot drift apart.

/** Identity of one month of one contract — what billing is keyed on. */
export function billingKey(contractId: string, period: string): string {
    return `${contractId}:${period}`;
}

/**
 * The (contract, month) pairs that have an invoice — of ANY status.
 *
 * Draft and Sent count here too. An unpaid invoice means the month IS billed
 * through the invoice, and the money simply has not arrived: it contributes
 * nothing to collected revenue, and its visits must not quietly stand in for
 * the payment that is still owed.
 */
export function billedPeriods(invoices: ContractInvoice[]): Set<string> {
    return new Set(invoices.map(i => billingKey(i.contractId, i.period)));
}

/**
 * El mes 'YYYY-MM' al que pertenece una visita, o '' si su fecha no sirve.
 *
 * Es el mismo recorte con el que contractBilling empareja visitas y periodos,
 * extraído aquí para que ni una sola función lo escriba por su cuenta: una
 * visita no puede estar facturada para un archivo y sin facturar para otro.
 */
export function visitPeriod(visit: ServiceVisit): string {
    const period = (visit.date || '').slice(0, 7);
    return period.length === 7 ? period : '';
}

/** True when this visit falls in a month already billed by an invoice. */
export function visitIsBilled(visit: ServiceVisit, billed: Set<string>): boolean {
    if (billed.size === 0) return false;
    const period = visitPeriod(visit);
    return period !== '' && billed.has(billingKey(visit.contractId, period));
}

// ── La unidad de un contrato Monthly es el MES, no la visita ─────
//
// El mismo mes de la misma ruta valía dos cosas distintas según quién mirara:
//
//   perVisitRevenue (Finanzas)  → monthlyRate / 4,33, y eso POR CADA visita
//                                 completada que hubiera apuntada.
//   amountForPeriod (Rutas ▸ Cobros, utils/contractBilling.ts:57)
//                               → monthlyRate plano, que es lo que se imprime
//                                 en la factura que recibe el cliente.
//
// Ninguna de las dos estaba mal escrita; contaban cosas distintas y las dos se
// llamaban «ingreso de rutas».
//
// MEDIDO CONTRA PRODUCCIÓN EL 11-AGO-2026, sobre los 20 meses que tienen trabajo
// apuntado y ninguna factura (22 visitas en total, 149 contratos mensuales):
//   por visita (lo que publicaba Finanzas)  $977,92
//   por mes    (lo que dice la factura)   $2.947,00     → factor 3,01
// Casi todo cae en un solo espacio de trabajo: 15 meses, 17 visitas, $850,48
// contra $2.595,00 — el 3,05 del informe. Los otros tres espacios afectados
// suben de $39,72 a $172, de $27,71 a $120 y de $0 a $0. El único espacio con
// contratos PerVisit no se mueve ni un centavo ($60 antes y después), que es
// exactamente lo que tiene que pasar.
//
// GANA EL MES, y no por gusto:
//   - Es lo que firma el cliente. Un contrato mensual vende COBERTURA, no
//     visitas: el mes se paga entero pase la cuadrilla cuatro veces o cinco.
//   - Es lo que dice el papel. La factura que sale de esta misma app cobra
//     monthlyRate; publicar otra cifra es contradecir un documento enviado.
//   - Es la única que no se mueve sola. Con 4,33 un mes de cinco semanas
//     «ingresaba» un 15 % de más y uno flojo un 30 % de menos, sin que nada
//     hubiera cambiado en el contrato.
//   - 4,33 es un PROMEDIO de calendario. Sirve para pronosticar (ahí sigue, en
//     contractMRR); repartir con él dinero ya ganado inventa un precio por
//     visita que ninguna factura va a respaldar.
//
// UN CONTRATO PerVisit NO SE TOCA: ahí la visita sí es la unidad, se cobra
// visita a visita y los dos motores ya coincidían. Cambiarlo también habría
// movido el dinero de sitio dentro del mes en el gráfico semanal, que reparte
// por día.
//
// LA PRECEDENCIA DE SIEMPRE SIGUE INTACTA: si el mes tiene factura, manda la
// factura (sea cual sea su estado). Lo de aquí sólo pone precio a los meses que
// nadie facturó.
//
// LO QUE A PROPÓSITO NO SE HACE: valorar los meses SIN visitas apuntadas. Un
// contrato mensual devenga aunque la cuadrilla no pase, y `contractCoversPeriod`
// sabe decir qué meses cubre — pero recorrer el calendario desde `startDate`
// publicaría meses que nadie trabajó ni facturó, que es exactamente el vicio que
// esta campaña vino a quitar. Un mes sin trabajo apuntado y sin factura vale 0 y
// se ve como lo que es: un mes por facturar que nadie registró.

/** Un mes de un contrato: lo que valió el trabajo, y si alguien lo facturó. */
export interface ContractPeriod {
    contractId: string;
    /** Mes natural, 'YYYY-MM'. Es la identidad de la facturación. */
    period: string;
    visitsCompleted: number;
    /** Las fechas de esas visitas, en orden. Vacío en un mes que sólo tiene
     *  factura — que existe, porque un mensual factura sin pasar nadie. */
    visitDates: string[];
    /** Lo que vale el TRABAJO del mes, preguntado a `amountForPeriod`: plano si
     *  el contrato es mensual, visitas × tarifa si se cobra por visita. */
    workValue: number;
    /** true en cuanto hay una factura de ese mes, pagada o no. Es lo que separa
     *  «facturado» de «por facturar» en pantalla. */
    invoiced: boolean;
    invoicedAmount: number;
    /** Sólo lo que está marcado Paid: dinero en mano, no dinero prometido. */
    paidAmount: number;
    /** Lo que cuenta como ganado. Manda la factura cuando existe; sólo cuando
     *  nadie facturó habla el trabajo. */
    earned: number;
}

/**
 * El mes a mes de un contrato: uno por cada mes con trabajo hecho y uno por cada
 * mes facturado, ordenados del más viejo al más nuevo.
 *
 * Una visita completada sin fecha utilizable NO entra en ningún mes: sin mes no
 * hay unidad con la que valorarla. No desaparece en silencio — quien llame tiene
 * `undatedCompletedVisits` para contarlas y decirlo.
 */
export function contractPeriods(
    contract: ServiceContract,
    visits: ServiceVisit[],
    invoices: ContractInvoice[] = [],
): ContractPeriod[] {
    const misVisitas = visits.filter(v => v.contractId === contract.id && v.status === 'Completed');

    const fechasPorMes = new Map<string, string[]>();
    for (const v of misVisitas) {
        const period = visitPeriod(v);
        if (!period) continue;
        const fechas = fechasPorMes.get(period);
        if (fechas) fechas.push(v.date);
        else fechasPorMes.set(period, [v.date]);
    }

    const facturasPorMes = new Map<string, { invoicedAmount: number; paidAmount: number }>();
    for (const i of invoices) {
        if (i.contractId !== contract.id) continue;
        // El periodo de la factura se toma tal cual, sin validar el formato: si
        // una fila trae un periodo raro seguirá teniendo su mes propio y su
        // importe seguirá sumando. Descartarla aquí evaporaría dinero facturado.
        const acc = facturasPorMes.get(i.period) || { invoicedAmount: 0, paidAmount: 0 };
        acc.invoicedAmount += i.amount || 0;
        if (i.status === 'Paid') acc.paidAmount += i.amount || 0;
        facturasPorMes.set(i.period, acc);
        if (!fechasPorMes.has(i.period)) fechasPorMes.set(i.period, []);
    }

    return [...fechasPorMes.entries()]
        .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
        .map(([period, visitDates]) => {
            const factura = facturasPorMes.get(period);
            // `misVisitas` ya está filtrado por contrato y por estado con el mismo
            // criterio que aplica amountForPeriod, así que pasarle esta lista o la
            // entera da la misma cifra — y le ahorra recorrer las de todo el mundo.
            const workValue = amountForPeriod(contract, period, misVisitas);
            return {
                contractId: contract.id,
                period,
                visitsCompleted: visitDates.length,
                visitDates: [...visitDates].sort(),
                workValue,
                invoiced: factura !== undefined,
                invoicedAmount: factura?.invoicedAmount ?? 0,
                paidAmount: factura?.paidAmount ?? 0,
                earned: factura ? factura.invoicedAmount : workValue,
            };
        });
}

/** Visitas completadas cuya fecha no cae en ningún mes: no se pueden valorar. */
export function undatedCompletedVisits(contract: ServiceContract, visits: ServiceVisit[]): number {
    return visits.filter(v =>
        v.contractId === contract.id && v.status === 'Completed' && visitPeriod(v) === ''
    ).length;
}

/** Un trozo de trabajo ganado y sin facturar, ya valorado. */
export interface EarnedUnit {
    contractId: string;
    period: string;
    /** Cuándo se ganó, ISO. En un mensual es la ÚLTIMA visita del mes: el mes se
     *  cobra por el trabajo hecho, así que no puede aparecer en el gráfico antes
     *  de que ese trabajo esté hecho. */
    date: string;
    amount: number;
    /** Cuántas visitas hay detrás: 1 por visita, las del mes en un mensual. */
    visitsCompleted: number;
    /** Con qué unidad se valoró. Existe para que una pantalla pueda explicar por
     *  qué cinco visitas de un mensual son una sola línea. */
    unit: 'visit' | 'month';
}

/**
 * Lo ganado que NINGUNA factura cubre, cada cosa en su unidad natural.
 *
 * Es la única puerta por la que el trabajo sin facturar entra en una cifra de
 * dinero: el gráfico de ingresos y los totales de Finanzas llaman aquí, así que
 * no pueden discrepar ni entre ellos ni con la factura que emite Cobros.
 */
export function unbilledEarnings(
    contract: ServiceContract,
    visits: ServiceVisit[],
    invoices: ContractInvoice[] = [],
): EarnedUnit[] {
    const units: EarnedUnit[] = [];
    const porVisita = contract.billingType !== 'Monthly';

    for (const p of contractPeriods(contract, visits, invoices)) {
        if (p.invoiced) continue;          // la factura ya habló por este mes
        if (p.visitsCompleted === 0) continue;  // mes sin trabajo apuntado: no se inventa
        if (porVisita) {
            for (const date of p.visitDates) {
                units.push({
                    contractId: contract.id,
                    period: p.period,
                    date,
                    amount: perVisitRevenue(contract),
                    visitsCompleted: 1,
                    unit: 'visit',
                });
            }
            continue;
        }
        units.push({
            contractId: contract.id,
            period: p.period,
            date: p.visitDates[p.visitDates.length - 1],
            amount: p.workValue,
            visitsCompleted: p.visitsCompleted,
            unit: 'month',
        });
    }

    return units;
}

// ── Lo que cuesta ir ────────────────────────────────────────────
//
// Finanzas publica el MRR de una ruta como si fuera ganancia limpia: nunca resta
// lo que cuesta mandar la cuadrilla. Y el dato para restarlo está desde siempre.
//
// MEDIDO CONTRA PRODUCCIÓN EL 11-AGO-2026:
//   186 de 186 contratos traen `estimatedDurationMinutes`. Media 34,2 min.
//    34 visitas traen `startedAt` y `completedAt` — tiempo MEDIDO...
//    15 de esas 34 duran CERO minutos.
//
// Ese cero no es una visita relámpago: los dos sellos son distintos en las 34,
// así que son visitas abiertas y cerradas dentro del mismo minuto. Es un toque
// en la pantalla, no una medición de trabajo.
//
// Y LOS QUINCE CEROS DECIDEN EL SIGNO DE LA CONCLUSIÓN:
//   media con los ceros dentro   22,4 min  <  34,2 estimados → «se tarda MENOS
//                                             de lo previsto: sobra margen»
//   media sin los ceros          40,0 min  >  34,2 estimados → «se tarda MÁS
//                                             de lo previsto: falta margen»
// La misma base sostiene las dos frases contrarias según si se promedian quince
// ceros. Aquí se descartan, por el motivo de siempre en esta campaña: un cero
// promediado abarata la visita, y abaratar la visita infla el margen. Cuando un
// dato no se sabe leer, el error tiene que caer del lado prudente.
//
// Cuántos se tiraron viaja en `visitasDescartadas`, para que la pantalla pueda
// decirlo en voz alta en vez de esconderlo dentro de un promedio.

/** Lo que cuesta servir una ruta un mes, y lo que queda después. */
export interface CostoRuta {
    /** Lo que el contrato dice que dura una visita. 0 si no lo dice. */
    minutosEstimados: number;
    /** La media MEDIDA de las visitas cronometradas útiles, o null si no hay
     *  ninguna. null NO es 0: es «nadie ha cronometrado esta ruta todavía». */
    minutosReales: number | null;
    /** Cuántas visitas hay DETRÁS de `minutosReales`. Una media de dos visitas
     *  y una de veinte no se pueden enseñar igual. */
    visitasCronometradas: number;
    /** Cronometradas que se tiraron por durar 0 minutos o menos (ver arriba).
     *  15 de las 34 de producción. Si esto es alto, la media de al lado se
     *  calculó sobre muchas menos visitas de las que el cronómetro sugiere. */
    visitasDescartadas: number;
    /** Los minutos con los que de verdad se costeó: los reales si los hay, los
     *  estimados si no. `baseReal` dice cuál de los dos fue. */
    minutosPorVisita: number;
    /** Visitas al mes según la cadencia: la MISMA cifra con la que `contractMRR`
     *  reparte el ingreso, para que costo e ingreso hablen del mismo mes. 0 en
     *  un contrato que no está activo — a nadie se le está visitando. */
    visitasAlMes: number;
    /** minutosPorVisita/60 × breakEvenRate. 0 si el taller no tiene tarifas. */
    costoPorVisita: number;
    /** costoPorVisita × visitasAlMes. */
    costoMensual: number;
    /** El ingreso del mes, preguntado a `contractMRR`. 0 si el contrato no está
     *  activo, igual que en `contractFinancials`: dos cifras del mismo contrato
     *  no pueden discrepar según quién pregunte. */
    mrr: number;
    /** mrr − costoMensual. */
    margenMensual: number;
    /** margenMensual como % del mrr. 0 sin ingreso: sin denominador no hay
     *  porcentaje que inventar. */
    margenPorcentaje: number;
    /** true si el costo salió de tiempo MEDIDO; false si de lo estimado. Es el
     *  campo por el que existe esta función. Con 34 visitas cronometradas en
     *  toda la base, casi todas las rutas van a salir en false, y presentar un
     *  costo estimado como si estuviera medido es la clase exacta de cifra
     *  segura y falsa que esta campaña vino a quitar. */
    baseReal: boolean;
    /** false cuando el taller no tiene `breakEvenRate`. Entonces los costos son
     *  0 porque NO SE SABEN, no porque ir sea gratis — y el margen que sale es
     *  el MRR entero, justo la mentira que se quería borrar. Quien pinte tiene
     *  que enseñar la falta de tarifas, nunca ese margen del 100 %. */
    costeable: boolean;
}

/** Un número utilizable, o el de reserva. Un `undefined`, un NaN o un Infinity
 *  metidos en una multiplicación contaminan todo lo que tocan después. */
const numeroUtil = (valor: unknown, porDefecto = 0): number =>
    typeof valor === 'number' && Number.isFinite(valor) ? valor : porDefecto;

/**
 * Los minutos MEDIDOS de una visita, o null si nadie la cronometró.
 *
 * Cronometrada = tiene `startedAt` y `completedAt`. La duración se toma de
 * `durationMinutes` cuando está —en producción coincide con la resta de los dos
 * sellos en las 34, sin una sola discrepancia— y se calcula de los sellos cuando
 * falta, para que una fila a medio guardar no se pierda.
 */
export function minutosMedidos(visit: ServiceVisit): number | null {
    if (!visit.startedAt || !visit.completedAt) return null;
    if (typeof visit.durationMinutes === 'number' && Number.isFinite(visit.durationMinutes)) {
        return visit.durationMinutes;
    }
    const inicio = Date.parse(visit.startedAt);
    const fin = Date.parse(visit.completedAt);
    if (Number.isNaN(inicio) || Number.isNaN(fin)) return null;
    return (fin - inicio) / 60000;
}

/**
 * Lo que cuesta servir una ruta un mes, y lo que sobra del MRR después.
 *
 * `rates` es el mismo objeto que devuelve `laborRatesFromMetrics` (LaborRates
 * encaja aquí tal cual). Se pide sólo `breakEvenRate` —el costo por hora-hombre
 * con el overhead ya dentro— y se pide estructuralmente para no importar
 * financeSummary, que ya importa este archivo.
 *
 * SIN TARIFAS NO SE INVENTA NADA: los costos salen en 0 y `costeable` en false.
 * Ese 0 significa «no se sabe», y quien lo lea como «no cuesta» vuelve a
 * publicar el margen del 100 % de siempre.
 */
export function costoDeRuta(
    contract: ServiceContract,
    visits: ServiceVisit[],
    rates: { breakEvenRate: number },
): CostoRuta {
    const minutosEstimados = Math.max(0, numeroUtil(contract.estimatedDurationMinutes));

    let suma = 0;
    let visitasCronometradas = 0;
    let visitasDescartadas = 0;
    for (const v of visits) {
        if (v.contractId !== contract.id) continue;
        // Mismo criterio que contractPeriods: sólo cuenta el trabajo hecho. El
        // cronómetro de una visita saltada no mide trabajo de nadie.
        if (v.status !== 'Completed') continue;
        const medidos = minutosMedidos(v);
        if (medidos === null) continue;
        if (medidos <= 0) { visitasDescartadas += 1; continue; }
        suma += medidos;
        visitasCronometradas += 1;
    }

    const minutosReales = visitasCronometradas > 0 ? suma / visitasCronometradas : null;
    const minutosPorVisita = minutosReales ?? minutosEstimados;

    // Un contrato pausado o cancelado no se visita: cero visitas al mes. Escrito
    // así —y no poniendo el costo a 0 a mano— el objeto sigue cuadrando solo:
    // costoMensual nunca deja de ser costoPorVisita × visitasAlMes.
    const activo = contract.status === 'Active';
    const visitasAlMes = activo ? (VISITS_PER_MONTH[contract.frequency] ?? 1) : 0;

    // Una tarifa negativa es un dato roto, no un descuento: se trata como
    // ausente para que jamás salga un costo por debajo de cero.
    const breakEvenRate = Math.max(0, numeroUtil(rates?.breakEvenRate));
    const costoPorVisita = (minutosPorVisita / 60) * breakEvenRate;
    const costoMensual = costoPorVisita * visitasAlMes;

    const mrr = activo ? contractMRR(contract) : 0;
    const margenMensual = mrr - costoMensual;

    return {
        minutosEstimados,
        minutosReales,
        visitasCronometradas,
        visitasDescartadas,
        minutosPorVisita,
        visitasAlMes,
        costoPorVisita,
        costoMensual,
        mrr,
        margenMensual,
        margenPorcentaje: mrr > 0 ? (margenMensual / mrr) * 100 : 0,
        baseReal: minutosReales !== null,
        costeable: breakEvenRate > 0,
    };
}

/** Cuántas visitas completadas tiene cada par (contrato, mes). */
export function completedVisitsByPeriod(visits: ServiceVisit[]): Map<string, number> {
    const cuenta = new Map<string, number>();
    for (const v of visits) {
        if (v.status !== 'Completed') continue;
        const period = visitPeriod(v);
        if (!period) continue;
        const key = billingKey(v.contractId, period);
        cuenta.set(key, (cuenta.get(key) || 0) + 1);
    }
    return cuenta;
}

/**
 * Lo que le toca a UNA visita cuando hay que enseñarla en una fila suelta.
 *
 * Una lista de trabajos necesita un importe por fila, y un mensual no tiene
 * precio por visita. Repartir el mes entre las visitas que de verdad hubo es lo
 * único que hace que las filas de un mes sumen el mes: con 4,33 sumaban otra
 * cosa, y la resta se la comía el ojo del que leía.
 *
 * NO MIRA FACTURAS: quien las tenga a mano debe repartir `ContractPeriod.earned`
 * y no esto.
 */
export function visitShareOfMonth(
    contract: ServiceContract,
    visit: ServiceVisit,
    completedByPeriod: Map<string, number>,
): number {
    if (visit.status !== 'Completed') return 0;
    if (contract.billingType !== 'Monthly') return perVisitRevenue(contract);
    const period = visitPeriod(visit);
    if (!period) return 0;
    const visitas = completedByPeriod.get(billingKey(visit.contractId, period)) || 0;
    if (visitas <= 0) return 0;
    return (contract.monthlyRate || 0) / visitas;
}
